import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { equipment } from '@/utils/api'
import type {
  Equipment, EquipmentDefect, EquipmentDetail, EquipmentMeta, EquipmentPlace, EquipmentStats,
} from '@/types/equipment'

export function useEquipment(params?: Record<string, any>) {
  return useQuery<Equipment[]>({
    queryKey: ['equipment', 'list', params],
    queryFn: () => equipment.getAll(params).then((r) => r.data),
    placeholderData: (prev) => prev,
  })
}

export function useEquipmentDetail(id: number | null) {
  return useQuery<EquipmentDetail>({
    queryKey: ['equipment', 'detail', id],
    queryFn: () => equipment.getById(id).then((r) => r.data),
    enabled: id != null,
  })
}

export function useEquipmentStats() {
  return useQuery<EquipmentStats>({
    queryKey: ['equipment', 'stats'],
    queryFn: () => equipment.getStats().then((r) => r.data),
    refetchInterval: 60_000,
  })
}

export function useEquipmentMeta() {
  return useQuery<EquipmentMeta>({
    queryKey: ['equipment', 'meta'],
    queryFn: () => equipment.getMeta().then((r) => r.data),
    staleTime: 5 * 60_000,
  })
}

export function useEquipmentDefects(scope: 'offen' | 'aussonderung' | 'alle') {
  return useQuery<EquipmentDefect[]>({
    queryKey: ['equipment', 'defects', scope],
    queryFn: () => equipment.getDefects(scope).then((r) => r.data),
    placeholderData: (prev) => prev,
  })
}

export function useEquipmentPlaces(all = false) {
  return useQuery<EquipmentPlace[]>({
    queryKey: ['equipment', 'places', all],
    queryFn: () => equipment.getPlaces(all).then((r) => r.data),
  })
}

export function useEquipmentCategories() {
  return useQuery({
    queryKey: ['equipment', 'categories'],
    queryFn: () => equipment.getCategories().then((r) => r.data),
  })
}

export function useDguvAlerts() {
  return useQuery({
    queryKey: ['equipment', 'dguv-alerts'],
    queryFn: () => equipment.getDguvAlerts().then((r) => r.data),
  })
}

/** Alle Geräte-Queries neu laden (Liste, Detail, Stats, Inbox). */
export function useInvalidateEquipment() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['equipment'] })
}

function errorMessage(err: any, fallback: string) {
  return err?.response?.data?.error || err?.message || fallback
}

/**
 * Generische Geräte-Mutation: führt `fn` aus, zeigt Erfolg/Fehler, lädt alle Geräte-Queries neu.
 */
export function useEquipmentAction<TArgs>(fn: (args: TArgs) => Promise<any>, successMsg?: string | ((data: any) => string)) {
  const invalidate = useInvalidateEquipment()
  return useMutation({
    mutationFn: (args: TArgs) => fn(args).then((r) => r.data),
    onSuccess: (data) => {
      if (successMsg) toast.success(typeof successMsg === 'function' ? successMsg(data) : successMsg)
      invalidate()
    },
    onError: (err: any) => toast.error(errorMessage(err, 'Aktion fehlgeschlagen')),
  })
}
